// ---------------------------------------------------------------------------
// Validación de datos de entrada (números, teléfonos, textos).
//
// Por qué existe: los campos <input type="number"> del navegador PARECEN
// seguros pero no lo son. Dejan escribir "e", "+", "-" (notación científica),
// no respetan min/max si el formulario se guarda con un botón propio, y cuando
// el contenido queda inválido devuelven TEXTO VACÍO en vez de un error. Eso,
// junto con parseFloat(x) || 0, hace que un gasto de "1,500" se guarde como
// L 0 sin avisarle a nadie — y el reporte sale mal.
//
// Aquí limpiamos el texto MIENTRAS se escribe: el dato entra correcto o no
// entra. Todo lo que guarda dinero o cantidades debe pasar por acá.
// ---------------------------------------------------------------------------

// Limpia lo que la persona escribe en un campo numérico.
// Devuelve { valor, rechazado }:
//   valor     → el texto ya limpio, listo para mostrar en el campo
//   rechazado → true si se quitó algo (para avisar "solo números")
//
// Acepta la coma como separador decimal (en Honduras se escribe 1,500 y
// también 10,50) y quita los separadores de miles.
export function limpiarNumero(texto, { decimales = 2, negativos = false } = {}) {
  const original = String(texto ?? '');
  if (original === '') return { valor: '', rechazado: false };

  let t = original;

  // El signo menos solo vale al inicio y solo si el campo lo permite.
  const negativo = negativos && /^\s*-/.test(t);
  t = t.replace(/-/g, '');

  // "1,500.00" → "1500.00"  (coma de miles)
  // "10,50"    → "10.50"    (coma decimal)
  if (t.includes(',')) {
    const soloComa = !t.includes('.');
    const ultima = t.lastIndexOf(',');
    const decTrasComa = t.length - ultima - 1;
    if (soloComa && decTrasComa > 0 && decTrasComa <= 2 && decimales > 0) {
      t = t.slice(0, ultima) + '.' + t.slice(ultima + 1); // coma decimal
    }
    t = t.replace(/,/g, ''); // el resto eran miles
  }

  // Fuera todo lo que no sea dígito o punto (letras, e/E, +, $, L, espacios…).
  t = t.replace(/[^0-9.]/g, '');

  // Un solo punto decimal: "12.3.4" → "12.34"
  const partes = t.split('.');
  if (partes.length > 2) t = partes[0] + '.' + partes.slice(1).join('');

  // Campos enteros (cantidades, stock): sin punto.
  if (decimales === 0) {
    t = t.replace(/\./g, '');
  } else {
    // Máximo de decimales permitidos.
    const i = t.indexOf('.');
    if (i >= 0 && t.length - i - 1 > decimales) t = t.slice(0, i + 1 + decimales);
  }

  // "007" → "7"  (pero se respeta "0.5" y el "0" solo)
  t = t.replace(/^0+(?=\d)/, '');

  const valor = (negativo && t !== '' ? '-' : '') + t;
  // Se considera "rechazado" solo si se perdieron caracteres reales, no por
  // normalizar comas o ceros (eso es ayuda, no error).
  const rechazado = /[^0-9.,\-\s]/.test(original);
  return { valor, rechazado };
}

// Texto del campo → número real. Devuelve null si no hay un número válido
// (NO devuelve 0: un campo vacío y un cero son cosas distintas).
export function aNumero(texto) {
  if (texto === null || texto === undefined || texto === '') return null;
  const n = Number(String(texto).replace(/,/g, ''));
  return Number.isFinite(n) ? n : null;
}

// Igual que aNumero pero con valor por defecto. Para cálculos en pantalla.
export function numeroSeguro(texto, porDefecto = 0) {
  const n = aNumero(texto);
  return n === null ? porDefecto : n;
}

// Revisa que el número esté dentro del rango. Devuelve el mensaje de error
// (texto amigable) o null si está bien.
export function validarRango(valor, { min, max, requerido = false, decimales = 2, nombre = 'valor' } = {}) {
  if (valor === '' || valor === null || valor === undefined) {
    return requerido ? `Escribe ${nombre === 'valor' ? 'un valor' : 'el ' + nombre}` : null;
  }
  const n = aNumero(valor);
  if (n === null) return 'Solo se pueden escribir números en esta casilla';
  if (decimales === 0 && !Number.isInteger(n)) return 'Tiene que ser un número entero (sin decimales)';
  if (min !== undefined && n < min) {
    if (min === 0) return 'No puede ser negativo';
    if (min === 1) return 'Tiene que ser 1 o más';
    return `No puede ser menor que ${min}`;
  }
  if (max !== undefined && n > max) return `No puede pasar de ${max}`;
  return null;
}

// Tope de seguridad para montos en lempiras: evita que un dedazo
// (ej. pegar un número gigante) descuadre los reportes.
export const MONTO_MAXIMO = 99999999; // L 99,999,999

// Último filtro ANTES de guardar en la base (validación de salida).
//
// Antes se usaba parseFloat(monto) || 0: si el dato venía mal, se guardaba
// un CERO en silencio y el reporte salía mal sin que nadie se enterara.
// Ahora se corta el guardado con un mensaje claro, que la pantalla muestra.
//
// Devuelve el número redondeado a 2 decimales (centavos).
export function montoParaBD(valor, { nombre = 'monto', permitirCero = false, negativos = false } = {}) {
  const n = aNumero(valor);
  if (n === null) throw errorDato(`El ${nombre} tiene que ser un número (revisa que no tenga letras)`);
  if (!negativos && n < 0) throw errorDato(`El ${nombre} no puede ser negativo`);
  if (!permitirCero && n === 0) throw errorDato(`El ${nombre} tiene que ser mayor que cero`);
  if (Math.abs(n) > MONTO_MAXIMO) throw errorDato(`El ${nombre} es demasiado grande, revisalo`);
  return Math.round(n * 100) / 100;
}

// Cantidades: enteras por defecto (no existen 2.5 blusas). Los productos
// que se venden por peso pasan `decimales` > 0 (12.5 lb de pollo).
export function cantidadParaBD(valor, { nombre = 'cantidad', min, max = 100000, decimales = 0 } = {}) {
  const n = aNumero(valor);
  const minimo = min ?? (decimales > 0 ? 0.01 : 1);
  if (n === null) throw errorDato(`La ${nombre} tiene que ser un número`);
  if (decimales === 0 && !Number.isInteger(n)) throw errorDato(`La ${nombre} tiene que ser un número entero (ej. 3, no 3.5)`);
  if (n < minimo) throw errorDato(`La ${nombre} tiene que ser ${minimo} o más`);
  if (n > max) throw errorDato(`Esa ${nombre} parece un error (máximo ${max})`);
  return decimales > 0 ? Math.round(n * 1000) / 1000 : n;
}

function errorDato(mensaje) {
  const e = new Error(mensaje);
  e.code = 'DATO_INVALIDO';
  return e;
}

// --- Teléfonos ------------------------------------------------------------
// Deja solo lo que sirve para marcar o abrir WhatsApp: dígitos, + al inicio,
// y separadores comunes. Si el teléfono trae letras, el link de WhatsApp falla.
export function limpiarTelefono(texto) {
  let t = String(texto ?? '').replace(/[^0-9+\-\s()]/g, '');
  t = t.replace(/(?!^)\+/g, '');   // el + solo al inicio
  t = t.replace(/\s{2,}/g, ' ');   // sin espacios dobles (quedan al quitar letras)
  t = t.replace(/^[\s-]+/, '');    // sin espacios ni guiones al inicio
  return t.slice(0, 20);
}

// Un teléfono utilizable necesita al menos 8 dígitos (Honduras usa 8).
export function telefonoValido(texto) {
  const digitos = String(texto ?? '').replace(/\D/g, '');
  return digitos.length >= 8;
}

// --- Textos ---------------------------------------------------------------
// Quita espacios de sobra y recorta a un largo máximo, para que un nombre
// pegado por error no rompa las listas, etiquetas ni los reportes.
export function limpiarTexto(texto, max = 120) {
  return String(texto ?? '').replace(/\s+/g, ' ').trimStart().slice(0, max);
}

// Correo válido (sencillo pero suficiente). Vacío = válido, es opcional.
export function emailValido(texto) {
  const t = String(texto ?? '').trim();
  if (!t) return true;
  return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(t);
}
